import { FormatDateTimeBr, FormatIsoDate } from '../../utils/date';

/**
 * Exibição de valores com FORMATO FIXO — identificadores e datas.
 *
 * ── A regra ─────────────────────────────────────────────────────────────────
 * Todo dado que segue uma máscara conhecida (MLB, SKU, id de pedido, data,
 * data-hora) vai em monoespaçada. O motivo é comparação vertical: numa lista de
 * MLBs em fonte proporcional, `MLB4627310495` e `MLB2154849930` terminam em
 * colunas diferentes, e o olho não consegue varrer a diferença. Em monoespaçada
 * cada caractere ocupa a mesma largura, os campos empilham alinhados e a
 * diferença entre dois valores salta.
 *
 * O contrário também vale: TÍTULO de anúncio, nome de campanha e texto livre NÃO
 * entram aqui. Monoespaçada em texto corrido piora a leitura — ela serve para
 * comparar, não para ler.
 *
 * ── Por que componentes e não uma classe ────────────────────────────────────
 * `font-mono` estava em 122 lugares, cada um com um tamanho e uma cor. O ponto
 * destes componentes é que a decisão de estilo mora num arquivo só: mudar o
 * tratamento de todo MLB do sistema passa a ser mudar uma linha.
 */

/** Classe base. Exportada para os poucos casos que precisam compor à mão. */
export const MONO_CLASS = 'font-mono tabular-nums';

const juntar = (...partes) => partes.filter(Boolean).join(' ');

/**
 * Base monoespaçada. `title` recebe o valor cheio por padrão: identificador
 * truncado sem forma de ver o original é uma armadilha.
 */
export const Mono = ({ children, className = '', title, as: Tag = 'span', ...resto }) => (
    <Tag
        className={juntar(MONO_CLASS, className)}
        title={title ?? (typeof children === 'string' ? children : undefined)}
        {...resto}
    >
        {children}
    </Tag>
);

/** Identificador genérico: id de pedido, inventory, chave de NF-e. */
export const Id = ({ value, className = '', blank = '—', ...resto }) =>
    value ? (
        <Mono className={className} {...resto}>
            {value}
        </Mono>
    ) : (
        <span className="text-mono-400">{blank}</span>
    );

/**
 * Anúncio do Mercado Livre. Vira link quando há `permalink` — o MLB é o dado que
 * mais leva alguém a querer abrir o anúncio, e obrigar a copiar e colar era
 * atrito à toa.
 */
export const ItemId = ({ value, permalink = null, className = '', blank = '—' }) => {
    if (!value) {
        return <span className="text-mono-400">{blank}</span>;
    }
    if (!permalink) {
        return <Mono className={className}>{value}</Mono>;
    }
    return (
        <a
            href={permalink}
            target="_blank"
            rel="noopener noreferrer"
            title={`Abrir ${value} no Mercado Livre`}
            className={juntar(MONO_CLASS, 'text-link-600 hover:underline dark:text-link-400', className)}
        >
            {value}
        </a>
    );
};

/** SKU do vendedor. */
export const Sku = ({ value, className = '', blank = '—' }) =>
    value ? (
        <Mono className={className}>{value}</Mono>
    ) : (
        <span className="text-mono-400">{blank}</span>
    );

/** Data (sem hora), no formato brasileiro. */
export const DateText = ({ value, className = '', blank = '—' }) => (
    <Mono className={className} title={value || undefined}>
        {FormatIsoDate(value, { blank })}
    </Mono>
);

/**
 * Distância até agora, em forma curta. Fica em caixa de largura MÍNIMA e
 * alinhada à direita — é o que permite que o prefixo de várias linhas empilhe
 * sem empurrar a data de cada uma para um x diferente.
 */
const RELATIVO_MIN_W = 'inline-block min-w-[62px] text-right';

export function FormatRelativo(value) {
    if (!value) {
        return null;
    }
    const ms = Date.now() - new Date(value).getTime();
    if (Number.isNaN(ms)) {
        return null;
    }
    const min = Math.floor(Math.abs(ms) / 60000);
    const futuro = ms < 0;
    const texto =
        min < 1 ? 'agora' : min < 60 ? `${min}m` : min < 1440 ? `${Math.floor(min / 60)}h` : `${Math.floor(min / 1440)}d`;
    if (texto === 'agora') {
        return 'agora';
    }
    return futuro ? `em ${texto}` : `${texto} atrás`;
}

/**
 * Data e hora. Com `relative`, o tempo relativo vem ANTES da data — pedido do
 * dono, e o motivo é simetria: o relativo tem largura variável ("agora",
 * "12m atrás", "3d atrás") e, deixado no fim, fazia a linha terminar ragged
 * justamente onde a monoespaçada deveria alinhar. Na frente, dentro de uma caixa
 * de largura mínima alinhada à direita, ele não desloca a data — as duas colunas
 * ficam alinhadas.
 */
export const DateTimeText = ({ value, relative = false, className = '', blank = '—' }) => {
    const formatada = FormatDateTimeBr(value, { blank });
    if (!relative || !value) {
        return (
            <Mono className={className} title={value || undefined}>
                {formatada}
            </Mono>
        );
    }
    return (
        <span className={juntar('inline-flex items-baseline gap-1.5', className)} title={value}>
            <span className={juntar(MONO_CLASS, RELATIVO_MIN_W, 'text-mono-400')}>({FormatRelativo(value)})</span>
            <Mono>{formatada}</Mono>
        </span>
    );
};
